/**
 * ZEN ERP — WhatsApp İç Mesajlaşma (WhatsApp Web Kalitesinde)
 * Meta Cloud API + Green API destekli tam özellikli mesajlaşma ekranı.
 */
import React, {
  useState, useEffect, useRef, useCallback, useContext, createContext,
} from "react";
import {
  MessageCircle, Send, Bot, User, ChevronLeft, Loader2, AlertCircle,
  Wifi, WifiOff, QrCode, Smartphone, CheckCircle2, RefreshCw, Search,
  Paperclip, Image, FileText, Music, ClipboardList, X, Plus, Check, CheckCheck,
  Phone, MoreVertical, Smile, Mic, Download, ExternalLink, ZoomIn,
  Clock, Archive, BellOff,
} from "lucide-react";
import { toast } from "sonner";
import api from "../api";

// ─── Yardımcı fonksiyonlar ───────────────────────────────────────────────────
function timeFmt(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
    if (diffDays === 1) return "Dün";
    if (diffDays < 7) return d.toLocaleDateString("tr-TR", { weekday: "long" });
    return d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "2-digit" });
  } catch { return ""; }
}

function dateSep(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffDays = Math.floor((now - d) / 86400000);
    if (diffDays === 0) return "Bugün";
    if (diffDays === 1) return "Dün";
    return d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return ""; }
}

function sameDay(a, b) {
  if (!a || !b) return false;
  return new Date(a).toDateString() === new Date(b).toDateString();
}

function initials(name) {
  const parts = (name || "?").trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.charAt(0).toUpperCase();
}

const AVATAR_COLORS = [
  "#25D366", "#128C7E", "#075E54", "#34B7F1",
  "#00BCD4", "#8BC34A", "#FF9800", "#9C27B0",
];
function avatarColor(phone) {
  let h = 0;
  for (let i = 0; i < (phone || "").length; i++) h = (h * 31 + phone.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

// ─── Kontekst ────────────────────────────────────────────────────────────────
const WaCtx = createContext(null);

// ─── Ana Sayfa ────────────────────────────────────────────────────────────────
export default function WhatsAppInboxPage() {
  const [activeTab, setActiveTab] = useState("inbox");
  return (
    <div className="flex flex-col h-[calc(100vh-72px)]" style={{ background: "#111b21" }}>
      {/* Tab bar */}
      <div className="flex border-b shrink-0" style={{ borderColor: "#2a3942", background: "#202c33" }}>
        {[
          { id: "inbox", icon: MessageCircle, label: "Gelen Kutusu" },
          { id: "qr",    icon: QrCode,        label: "Cihaz Bağla" },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            data-testid={`wa-tab-${t.id}`}
            className="flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors"
            style={{
              borderColor: activeTab === t.id ? "#25D366" : "transparent",
              color: activeTab === t.id ? "#25D366" : "#8696a0",
            }}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>
      {activeTab === "inbox" ? <InboxApp /> : <QRLinkView />}
    </div>
  );
}

// ─── Inbox Ana Uygulama ───────────────────────────────────────────────────────
function InboxApp() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);

  const selectedConv = conversations.find(c => c.phone === selectedPhone) || null;

  const loadConversations = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/whatsapp/inbox/conversations");
      setConversations(Array.isArray(data) ? data : []);
    } catch {
      if (!silent) toast.error("Konuşmalar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (phone, silent = false) => {
    if (!phone) return;
    if (!silent) setMsgLoading(true);
    try {
      const { data } = await api.get(`/whatsapp/inbox/messages/${encodeURIComponent(phone)}`);
      setMessages(data.messages || []);
    } catch {
      if (!silent) toast.error("Mesajlar yüklenemedi");
    } finally {
      setMsgLoading(false);
    }
  }, []);

  // İlk yükleme
  useEffect(() => {
    loadConversations();
    api.get("/quotes").then(r => setQuotes((r.data || []).filter(q => !q.isArchived && !q.archived))).catch(() => {});
    api.get("/whatsapp/inbox/quick-replies").then(r => setQuickReplies(r.data || [])).catch(() => {});
  }, [loadConversations]);

  // Auto-poll
  useEffect(() => {
    const t = setInterval(() => loadConversations(true), 12000);
    return () => clearInterval(t);
  }, [loadConversations]);

  useEffect(() => {
    if (!selectedPhone) return;
    loadMessages(selectedPhone);
    const t = setInterval(() => loadMessages(selectedPhone, true), 8000);
    return () => clearInterval(t);
  }, [selectedPhone, loadMessages]);

  const selectConv = phone => {
    setSelectedPhone(phone);
    setMobileView(true);
  };

  const filtered = conversations.filter(c =>
    !searchQ || c.name.toLowerCase().includes(searchQ.toLowerCase()) ||
    c.phone.includes(searchQ)
  );

  const ctxValue = {
    selectedPhone, selectedConv,
    messages, msgLoading, loadMessages, loadConversations,
    quotes, quickReplies,
    mobileView, setMobileView,
  };

  return (
    <WaCtx.Provider value={ctxValue}>
      <div className="flex flex-1 overflow-hidden">
        {/* LEFT — chat list */}
        <div
          className={`flex flex-col border-r shrink-0 transition-all ${mobileView ? "hidden md:flex" : "flex"}`}
          style={{ width: 340, borderColor: "#2a3942", background: "#111b21" }}
        >
          {/* List header */}
          <div className="flex items-center justify-between px-4 py-3 shrink-0" style={{ background: "#202c33" }}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white text-xs font-bold">
                <MessageCircle className="w-4 h-4" />
              </div>
              <span className="font-semibold text-sm text-white">WhatsApp</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="wa-new-chat-btn"
                onClick={() => setShowNewChat(true)}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                title="Yeni konuşma"
                style={{ color: "#aebac1" }}
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                onClick={() => loadConversations()}
                className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                style={{ color: "#aebac1" }}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="px-3 py-2 shrink-0" style={{ background: "#111b21" }}>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: "#202c33" }}>
              <Search className="w-3.5 h-3.5" style={{ color: "#8696a0" }} />
              <input
                type="text"
                placeholder="Konuşma ara..."
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                className="bg-transparent flex-1 text-sm outline-none"
                style={{ color: "#e9edef", caretColor: "#25D366" }}
                data-testid="wa-search-input"
              />
              {searchQ && (
                <button onClick={() => setSearchQ("")} style={{ color: "#8696a0" }}>
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto" data-testid="wa-conv-list">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#25D366" }} />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 px-6 text-center gap-3">
                <MessageCircle className="w-10 h-10" style={{ color: "#3b4a54" }} />
                <p className="text-sm font-semibold" style={{ color: "#8696a0" }}>
                  {searchQ ? "Sonuç yok" : "Henüz mesaj yok"}
                </p>
                {!searchQ && (
                  <p className="text-xs" style={{ color: "#667781" }}>
                    Gelen mesajlar burada görünecek
                  </p>
                )}
              </div>
            ) : (
              filtered.map(conv => (
                <ConvItem
                  key={conv.phone}
                  conv={conv}
                  active={selectedPhone === conv.phone}
                  onClick={() => selectConv(conv.phone)}
                />
              ))
            )}
          </div>
        </div>

        {/* RIGHT — chat area */}
        <div className={`flex flex-col flex-1 min-w-0 ${!mobileView && !selectedPhone ? "hidden md:flex" : "flex"}`}
          style={{ background: "#0b141a" }}>
          {!selectedPhone ? (
            <EmptyState onNewChat={() => setShowNewChat(true)} />
          ) : (
            <ChatArea />
          )}
        </div>
      </div>

      {showNewChat && (
        <NewChatModal onClose={() => setShowNewChat(false)} onStarted={phone => {
          setShowNewChat(false);
          selectConv(phone);
          loadConversations();
        }} />
      )}
    </WaCtx.Provider>
  );
}

// ─── Konuşma Listesi Öğesi ────────────────────────────────────────────────────
function ConvItem({ conv, active, onClick }) {
  const color = avatarColor(conv.phone);
  const isBot = conv.mode !== "manual";
  return (
    <button
      onClick={onClick}
      data-testid={`wa-conv-${conv.phone}`}
      className="w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors hover:bg-white/5"
      style={{ background: active ? "rgba(255,255,255,0.08)" : "transparent" }}
    >
      {/* Avatar */}
      <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 text-white text-sm font-bold"
        style={{ background: color }}>
        {initials(conv.name)}
      </div>
      {/* Info */}
      <div className="flex-1 min-w-0 border-b py-1" style={{ borderColor: active ? "transparent" : "#2a3942" }}>
        <div className="flex items-center justify-between mb-0.5">
          <span className="text-sm font-semibold truncate" style={{ color: "#e9edef" }}>{conv.name}</span>
          <span className="text-[11px] shrink-0 ml-2" style={{ color: conv.unreadCount > 0 ? "#25D366" : "#8696a0" }}>
            {timeFmt(conv.lastAt)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-1">
          <span className="text-xs truncate" style={{ color: "#8696a0" }}>
            {conv.lastDirection === "out" && <Check className="w-3 h-3 inline mr-0.5 text-emerald-400" />}
            {conv.lastMediaType === "image" ? "[Resim]" :
             conv.lastMediaType === "document" ? "[Belge]" :
             conv.lastMediaType === "audio" ? "[Ses]" :
             conv.lastMessage || ""}
          </span>
          <div className="flex items-center gap-1 shrink-0">
            {!isBot && (
              <span className="text-[9px] font-bold px-1 py-0.5 rounded"
                style={{ background: "rgba(245,166,35,0.2)", color: "#f5a623" }}>M</span>
            )}
            {conv.unreadCount > 0 && (
              <span className="text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center"
                style={{ background: "#25D366", color: "#111b21" }}>
                {conv.unreadCount > 9 ? "9+" : conv.unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

// ─── Boş Ekran ────────────────────────────────────────────────────────────────
function EmptyState({ onNewChat }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-5 px-8 text-center">
      <div className="w-24 h-24 rounded-full border-4 flex items-center justify-center"
        style={{ borderColor: "#2a3942" }}>
        <MessageCircle className="w-10 h-10" style={{ color: "#2a3942" }} />
      </div>
      <div>
        <p className="font-semibold text-base mb-1" style={{ color: "#e9edef" }}>ZEN WhatsApp</p>
        <p className="text-sm" style={{ color: "#8696a0" }}>
          Bir konuşma seçin veya yeni mesaj başlatın
        </p>
      </div>
      <button
        onClick={onNewChat}
        className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors"
        style={{ background: "#25D366", color: "#111b21" }}
      >
        <Plus className="w-4 h-4" /> Yeni Mesaj
      </button>
    </div>
  );
}

// ─── Aktif Sohbet Alanı ───────────────────────────────────────────────────────
function ChatArea() {
  const { selectedPhone, selectedConv, messages, msgLoading, loadMessages, loadConversations, quotes, quickReplies, setMobileView } = useContext(WaCtx);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [modeLoading, setModeLoading] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [showQuotePicker, setShowQuotePicker] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  // ── Akıllı scroll state'leri ──
  const [newMsgCount, setNewMsgCount] = useState(0);   // Kullanıcı yukarıdayken gelen mesaj sayısı
  const [isAtBottom, setIsAtBottom] = useState(true);  // Kullanıcı şu an en altta mı?
  const isFirstLoad = useRef(true);                     // İlk yükleme mi?
  const prevMsgCount = useRef(0);                       // Önceki mesaj sayısı
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);              // Kaydırılabilir alan ref'i
  const fileInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const docInputRef = useRef(null);
  const replyRef = useRef(null);

  const isManual = selectedConv?.mode === "manual";
  const color = avatarColor(selectedPhone);

  // Anlık scroll (animasyonsuz) — container tabanlı, scrollIntoView'dan daha stabil
  const _scrollInstant = useCallback(() => {
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  // Scroll pozisyonu takibi
  const handleScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const threshold = 80; // px — bu kadar alttaysa "en altta" sayılır
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    setIsAtBottom(atBottom);
    if (atBottom) setNewMsgCount(0); // Alttaysa badge'i temizle
  }, []);

  // Mesajlar değiştiğinde scroll mantığı (WhatsApp benzeri)
  useEffect(() => {
    const count = messages.length;
    if (count === 0) return;

    if (isFirstLoad.current) {
      // İlk yükleme: DOM güncellendikten sonra direkt en alta git
      requestAnimationFrame(_scrollInstant);
      isFirstLoad.current = false;
      prevMsgCount.current = count;
      return;
    }

    const added = count - prevMsgCount.current;
    prevMsgCount.current = count;

    if (added <= 0) return; // Silme veya aynı sayı

    if (isAtBottom) {
      // Kullanıcı zaten alttaysa → anlık scroll (smooth scroll zıplama yapar)
      requestAnimationFrame(_scrollInstant);
      setNewMsgCount(0);
    } else {
      // Kullanıcı yukarıdaysa → sadece badge güncelle
      setNewMsgCount(prev => prev + added);
    }
  }, [messages, isAtBottom, _scrollInstant]);

  // Konuşma değiştiğinde state sıfırla
  useEffect(() => {
    isFirstLoad.current = true;
    prevMsgCount.current = 0;
    setNewMsgCount(0);
    setIsAtBottom(true);
  }, [selectedPhone]);

  // "Aşağı in" butonuna basılınca — smooth scroll sadece burada
  const scrollToBottom = useCallback(() => {
    const el = scrollContainerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    setNewMsgCount(0);
  }, []);

  const toggleMode = async () => {
    const newMode = isManual ? "bot" : "manual";
    setModeLoading(true);
    try {
      await api.put(`/whatsapp/inbox/mode/${encodeURIComponent(selectedPhone)}`, { mode: newMode });
      await loadConversations(true);
      toast.success(newMode === "manual" ? "Manuel mod — bot susturuldu" : "Bot modu aktif");
    } catch { toast.error("Mod değiştirilemedi"); }
    finally { setModeLoading(false); }
  };

  const sendText = async (textOverride) => {
    const txt = (textOverride || replyText).trim();
    if (!txt || !selectedPhone) return;
    setSending(true);
    if (!textOverride) setReplyText("");
    try {
      await api.post("/whatsapp/inbox/reply", { phone: selectedPhone, text: txt });
      await loadMessages(selectedPhone);
      await loadConversations(true);
    } catch (e) {
      toast.error("Mesaj gönderilemedi: " + (e.response?.data?.detail || e.message));
      if (!textOverride) setReplyText(txt);
    } finally {
      setSending(false);
      replyRef.current?.focus();
    }
  };

  const sendFile = async (file, fileType) => {
    if (!file || !selectedPhone) return;
    setSending(true);
    setShowAttach(false);
    try {
      const fd = new FormData();
      fd.append("phone", selectedPhone);
      fd.append("caption", "");
      fd.append("file", file);
      await api.post("/whatsapp/inbox/send-media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${fileType === "image" ? "Resim" : fileType === "audio" ? "Ses" : "Dosya"} gönderildi`);
      await loadMessages(selectedPhone);
      await loadConversations(true);
    } catch (e) {
      toast.error("Dosya gönderilemedi: " + (e.response?.data?.detail || e.message));
    } finally { setSending(false); }
  };

  // Mesajları tarih separatörlü listele
  const msgGroups = [];
  messages.forEach((msg, i) => {
    const prevMsg = messages[i - 1];
    if (!prevMsg || !sameDay(prevMsg.at, msg.at)) {
      msgGroups.push({ type: "sep", label: dateSep(msg.at), key: `sep-${i}` });
    }
    msgGroups.push({ type: "msg", msg, key: msg.id || i });
  });

  return (
    <div className="flex flex-col h-full relative">
      {/* Chat header */}
      <div className="flex items-center gap-3 px-4 py-2.5 shrink-0" style={{ background: "#202c33" }}>
        <button onClick={() => setMobileView(false)} className="md:hidden p-1.5 rounded-full hover:bg-white/10" style={{ color: "#aebac1" }}>
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
          style={{ background: color }}>
          {initials(selectedConv?.name || selectedPhone)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate" style={{ color: "#e9edef" }}>
            {selectedConv?.name || selectedPhone}
          </p>
          <p className="text-[11px] font-mono" style={{ color: "#8696a0" }}>{selectedPhone}</p>
        </div>
        {/* Mode toggle */}
        <div className="flex items-center gap-2.5 shrink-0">
          <span className="text-xs font-semibold" style={{ color: isManual ? "#f5a623" : "#25D366" }}>
            {isManual ? "Manuel" : "Bot"}
          </span>
          <button
            onClick={toggleMode}
            disabled={modeLoading}
            data-testid="wa-mode-toggle"
            className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none"
            style={{ background: isManual ? "#f5a623" : "#25D366" }}
          >
            {modeLoading
              ? <Loader2 className="w-3 h-3 text-white animate-spin absolute left-1/2 -translate-x-1/2" />
              : <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isManual ? "translate-x-6" : "translate-x-1"}`} />
            }
          </button>
          {isManual ? <User className="w-4 h-4" style={{ color: "#f5a623" }} /> : <Bot className="w-4 h-4" style={{ color: "#25D366" }} />}
        </div>
      </div>

      {/* Bot modu bandı */}
      {!isManual && (
        <div className="flex items-center gap-2 px-4 py-1.5 text-xs shrink-0"
          style={{ background: "rgba(37,211,102,0.08)", borderBottom: "1px solid rgba(37,211,102,0.2)", color: "#25D366" }}>
          <Bot className="w-3.5 h-3.5 shrink-0" />
          <span>Bot modu aktif — gönderim için manuel moda geçin.</span>
        </div>
      )}

      {/* Messages */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-1"
        data-testid="wa-messages"
        style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.03) 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      >
        {msgLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#25D366" }} />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
            <Clock className="w-8 h-8" style={{ color: "#3b4a54" }} />
            <p className="text-sm" style={{ color: "#8696a0" }}>Henüz mesaj yok</p>
          </div>
        ) : (
          msgGroups.map(g =>
            g.type === "sep"
              ? <DateSeparator key={g.key} label={g.label} />
              : <MessageBubble key={g.key} msg={g.msg} onImageClick={setLightboxImg} />
          )
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Aşağı in butonu — kullanıcı yukarı kaydırdığında çıkar */}
      {!isAtBottom && (
        <div className="absolute bottom-20 right-4 z-20 flex flex-col items-center">
          {newMsgCount > 0 && (
            <div
              className="mb-1 px-2 py-0.5 rounded-full text-[11px] font-bold"
              style={{ background: "#25D366", color: "#111b21" }}
            >
              {newMsgCount} yeni
            </div>
          )}
          <button
            onClick={scrollToBottom}
            data-testid="wa-scroll-to-bottom"
            className="w-9 h-9 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110"
            style={{ background: "#202c33", border: "1px solid #3b4a54", color: "#25D366" }}
          >
            <ChevronLeft className="w-5 h-5 rotate-[-90deg]" />
          </button>
        </div>
      )}

      {/* Hızlı yanıtlar bandı */}
      {showQuickReplies && isManual && (
        <div className="px-3 py-2 shrink-0 border-t" style={{ background: "#1f2c33", borderColor: "#2a3942" }}>
          <div className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: "#8696a0" }}>
            Hazır Yanıtlar
          </div>
          <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
            {quickReplies.map(qr => (
              <button
                key={qr.id}
                onClick={() => { setReplyText(qr.text); setShowQuickReplies(false); replyRef.current?.focus(); }}
                className="px-2.5 py-1 rounded-full text-xs transition-colors"
                style={{ background: "#2a3942", color: "#e9edef" }}
              >
                {qr.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Reply bar */}
      <div className="px-3 py-2.5 shrink-0" style={{ background: "#202c33" }}>
        {!isManual ? (
          <div className="flex items-center justify-center gap-2 py-2 text-xs" style={{ color: "#8696a0" }}>
            <Bot className="w-3.5 h-3.5" />
            <span>Mesaj göndermek için Manuel moda geçin</span>
          </div>
        ) : (
          <div className="flex items-end gap-2">
            {/* Emoji / Quick Replies */}
            <button
              onClick={() => { setShowQuickReplies(p => !p); setShowAttach(false); }}
              className="p-2 rounded-full flex-shrink-0 transition-colors"
              style={{ color: showQuickReplies ? "#25D366" : "#8696a0" }}
              title="Hazır yanıtlar"
            >
              <Smile className="w-5 h-5" />
            </button>

            {/* Attachment menu */}
            <div className="relative flex-shrink-0">
              <button
                onClick={() => { setShowAttach(p => !p); setShowQuickReplies(false); }}
                className="p-2 rounded-full transition-colors"
                style={{ color: showAttach ? "#25D366" : "#8696a0" }}
                title="Dosya ekle"
                data-testid="wa-attach-btn"
              >
                <Paperclip className="w-5 h-5" />
              </button>
              {showAttach && (
                <AttachMenu
                  onClose={() => setShowAttach(false)}
                  onImage={() => { fileInputRef.current?.click(); setShowAttach(false); }}
                  onDoc={() => { docInputRef.current?.click(); setShowAttach(false); }}
                  onAudio={() => { audioInputRef.current?.click(); setShowAttach(false); }}
                  onQuote={() => { setShowQuotePicker(true); setShowAttach(false); }}
                />
              )}
            </div>

            {/* Hidden file inputs */}
            <input ref={fileInputRef} type="file" className="hidden" accept="image/*"
              onChange={e => { const f = e.target.files?.[0]; if (f) sendFile(f, "image"); e.target.value = ""; }} />
            <input ref={docInputRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
              onChange={e => { const f = e.target.files?.[0]; if (f) sendFile(f, "document"); e.target.value = ""; }} />
            <input ref={audioInputRef} type="file" className="hidden" accept="audio/*"
              onChange={e => { const f = e.target.files?.[0]; if (f) sendFile(f, "audio"); e.target.value = ""; }} />

            {/* Text input */}
            <textarea
              ref={replyRef}
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendText(); } }}
              placeholder="Mesaj yazın..."
              rows={1}
              className="flex-1 resize-none px-3 py-2 rounded-lg text-sm outline-none"
              style={{
                background: "#2a3942",
                color: "#e9edef",
                caretColor: "#25D366",
                border: "none",
                minHeight: 36,
                maxHeight: 120,
              }}
              data-testid="wa-reply-input"
            />

            {/* Send */}
            <button
              onClick={() => sendText()}
              disabled={!replyText.trim() || sending}
              className="p-2.5 rounded-full flex-shrink-0 transition-colors"
              style={{
                background: replyText.trim() ? "#25D366" : "#2a3942",
                color: replyText.trim() ? "#111b21" : "#8696a0",
              }}
              data-testid="wa-send-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        )}
      </div>

      {/* Teklif picker */}
      {showQuotePicker && (
        <QuotePickerModal
          phone={selectedPhone}
          quotes={quotes}
          onClose={() => setShowQuotePicker(false)}
          onSent={async () => {
            setShowQuotePicker(false);
            await loadMessages(selectedPhone);
            await loadConversations(true);
          }}
        />
      )}

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImg(null)}
        >
          <button className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white" onClick={() => setLightboxImg(null)}>
            <X className="w-5 h-5" />
          </button>
          <img src={lightboxImg} alt="Resim" className="max-w-full max-h-full rounded-lg object-contain" />
        </div>
      )}
    </div>
  );
}

// ─── Tarih Separatörü ─────────────────────────────────────────────────────────
function DateSeparator({ label }) {
  return (
    <div className="flex items-center justify-center my-3">
      <span className="px-3 py-1 rounded-full text-xs font-semibold"
        style={{ background: "#202c33", color: "#8696a0" }}>
        {label}
      </span>
    </div>
  );
}

// ─── Mesaj Baloncuğu ──────────────────────────────────────────────────────────
function MessageBubble({ msg, onImageClick }) {
  const isOut = msg.direction === "out";
  const hasMedia = !!msg.mediaType;
  const mediaUrl = msg.mediaId ? `/api/whatsapp/inbox/media/${msg.mediaId}` : msg.mediaUrl;

  return (
    <div className={`flex ${isOut ? "justify-end" : "justify-start"} mb-1`}>
      <div
        className="max-w-[72%] min-w-[80px] rounded-xl px-3 py-2 relative"
        style={{
          background: isOut ? "#005c4b" : "#202c33",
          borderRadius: isOut ? "12px 12px 0 12px" : "12px 12px 12px 0",
        }}
      >
        {/* Media content */}
        {hasMedia && msg.mediaType === "image" && mediaUrl && (
          <div className="mb-1">
            <img
              src={mediaUrl}
              alt="resim"
              className="rounded-lg max-w-full max-h-60 object-cover cursor-pointer"
              onClick={() => onImageClick(mediaUrl)}
              onError={e => { e.target.style.display = "none"; }}
            />
            {msg.mediaCaption && (
              <p className="text-xs mt-1" style={{ color: "#e9edef" }}>{msg.mediaCaption}</p>
            )}
          </div>
        )}
        {hasMedia && msg.mediaType === "document" && (
          <a
            href={mediaUrl || "#"}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-2.5 mb-1 p-2 rounded-lg"
            style={{ background: "rgba(255,255,255,0.07)" }}
          >
            <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: "#25D366" }}>
              <FileText className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold truncate" style={{ color: "#e9edef" }}>
                {msg.mediaFilename || "Belge"}
              </p>
              <p className="text-[10px]" style={{ color: "#8696a0" }}>
                {(msg.mediaMime || "application/pdf").split("/")[1]?.toUpperCase() || "DOSYA"}
              </p>
            </div>
            <Download className="w-4 h-4 shrink-0" style={{ color: "#8696a0" }} />
          </a>
        )}
        {hasMedia && msg.mediaType === "audio" && (
          <div className="flex items-center gap-2 mb-1 p-2 rounded-lg" style={{ background: "rgba(255,255,255,0.07)" }}>
            <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
              style={{ background: "#25D366" }}>
              <Mic className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1">
              {mediaUrl ? (
                <audio controls className="w-full h-7" style={{ filter: "invert(1) hue-rotate(180deg)" }}>
                  <source src={mediaUrl} type={msg.mediaMime || "audio/ogg"} />
                </audio>
              ) : (
                <p className="text-xs" style={{ color: "#8696a0" }}>Ses mesajı</p>
              )}
            </div>
          </div>
        )}
        {hasMedia && msg.mediaType === "video" && (
          <div className="mb-1">
            {mediaUrl ? (
              <video controls className="rounded-lg max-w-full max-h-48 w-full" style={{ maxWidth: 280 }}>
                <source src={mediaUrl} type={msg.mediaMime || "video/mp4"} />
              </video>
            ) : (
              <div className="flex items-center gap-2 text-xs" style={{ color: "#8696a0" }}>
                <FileText className="w-4 h-4" /> Video
              </div>
            )}
          </div>
        )}

        {/* Text */}
        {msg.text && !(hasMedia && !msg.text.startsWith("[")) && (
          <p className="text-sm leading-relaxed whitespace-pre-wrap break-words" style={{ color: "#e9edef" }}>
            {msg.text}
          </p>
        )}

        {/* Time + status */}
        <div className={`flex items-center gap-1 mt-1 ${isOut ? "justify-end" : "justify-start"}`}>
          <span className="text-[10px]" style={{ color: isOut ? "rgba(233,237,239,0.6)" : "#8696a0" }}>
            {timeFmt(msg.at)}
          </span>
          {isOut && (
            msg.status === "read" ? <CheckCheck className="w-3 h-3 text-sky-400" /> :
            msg.status === "delivered" ? <CheckCheck className="w-3 h-3" style={{ color: "rgba(233,237,239,0.6)" }} /> :
            <Check className="w-3 h-3" style={{ color: "rgba(233,237,239,0.6)" }} />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Ek Menüsü ────────────────────────────────────────────────────────────────
function AttachMenu({ onClose, onImage, onDoc, onAudio, onQuote }) {
  const items = [
    { icon: Image,        label: "Resim",          color: "#9c27b0", action: onImage },
    { icon: FileText,     label: "Belge / PDF",     color: "#2196f3", action: onDoc },
    { icon: Music,        label: "Ses Dosyası",     color: "#ff5722", action: onAudio },
    { icon: ClipboardList,label: "Teklif Gönder",   color: "#25D366", action: onQuote },
  ];
  return (
    <div
      className="absolute bottom-10 left-0 rounded-2xl overflow-hidden shadow-2xl z-30 py-2"
      style={{ background: "#233138", minWidth: 180, border: "1px solid #2a3942" }}
      data-testid="wa-attach-menu"
    >
      {items.map(item => (
        <button
          key={item.label}
          onClick={item.action}
          className="flex items-center gap-3 w-full px-4 py-2.5 text-sm transition-colors hover:bg-white/8"
          style={{ color: "#e9edef" }}
          data-testid={`wa-attach-${item.label.toLowerCase().replace(/ /g, "-")}`}
        >
          <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
            style={{ background: item.color }}>
            <item.icon className="w-4 h-4 text-white" />
          </div>
          {item.label}
        </button>
      ))}
    </div>
  );
}

// ─── Teklif Seçici Modal ─────────────────────────────────────────────────────
function QuotePickerModal({ phone, quotes, onClose, onSent }) {
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all"); // all | satis | alis

  const filtered = quotes.filter(q => {
    const matchType = typeFilter === "all" || q.brand === typeFilter ||
      (typeFilter === "satis" && !q.brand?.includes("alis")) ||
      (typeFilter === "alis" && q.brand?.includes("alis"));
    const matchSearch = !search ||
      q.customerName?.toLowerCase().includes(search.toLowerCase()) ||
      q.projectName?.toLowerCase().includes(search.toLowerCase()) ||
      String(q.quoteNo || "").includes(search);
    return matchType && matchSearch;
  }).slice(0, 40);

  const sendQuote = async (q) => {
    setSending(q.id);
    try {
      await api.post("/whatsapp/inbox/send-quote", { phone, quoteId: q.id });
      toast.success(`Teklif #${q.quoteNo || q.id} gönderildi`);
      onSent();
    } catch (e) {
      toast.error("Teklif gönderilemedi: " + (e.response?.data?.detail || e.message));
      setSending(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl flex flex-col"
        style={{ background: "#202c33", maxHeight: "80vh" }}
        onClick={e => e.stopPropagation()}
        data-testid="wa-quote-picker"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 shrink-0" style={{ borderBottom: "1px solid #2a3942" }}>
          <h3 className="font-bold text-base flex items-center gap-2" style={{ color: "#e9edef" }}>
            <ClipboardList className="w-5 h-5" style={{ color: "#25D366" }} />
            Teklif Gönder
          </h3>
          <button onClick={onClose} style={{ color: "#8696a0" }}><X className="w-5 h-5" /></button>
        </div>
        {/* Filter + Search */}
        <div className="px-4 py-3 space-y-2 shrink-0" style={{ borderBottom: "1px solid #2a3942" }}>
          <div className="flex gap-1.5">
            {[
              { v: "all", l: "Tümü" },
              { v: "satis", l: "Satış" },
              { v: "alis", l: "Alış" },
            ].map(f => (
              <button
                key={f.v}
                onClick={() => setTypeFilter(f.v)}
                className="px-3 py-1 rounded-full text-xs font-semibold transition-colors"
                style={{
                  background: typeFilter === f.v ? "#25D366" : "#2a3942",
                  color: typeFilter === f.v ? "#111b21" : "#8696a0",
                }}
              >
                {f.l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: "#2a3942" }}>
            <Search className="w-3.5 h-3.5" style={{ color: "#8696a0" }} />
            <input
              type="text"
              placeholder="Müşteri, proje veya teklif no ara..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="bg-transparent flex-1 text-sm outline-none"
              style={{ color: "#e9edef" }}
              autoFocus
            />
          </div>
        </div>
        {/* Quote list */}
        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <ClipboardList className="w-8 h-8" style={{ color: "#3b4a54" }} />
              <p className="text-sm" style={{ color: "#8696a0" }}>Teklif bulunamadı</p>
            </div>
          ) : (
            filtered.map(q => (
              <div
                key={q.id}
                className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-white/5"
                style={{ borderBottom: "1px solid #1a2428" }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 text-white text-xs font-bold"
                  style={{ background: "#005c4b" }}>
                  #{q.quoteNo || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: "#e9edef" }}>
                    {q.customerName}
                  </p>
                  <p className="text-xs truncate" style={{ color: "#8696a0" }}>
                    {q.projectName || "Proje belirtilmedi"} · {(q.total || 0).toLocaleString("tr-TR")} {q.currency}
                  </p>
                </div>
                <button
                  onClick={() => sendQuote(q)}
                  disabled={!!sending}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors shrink-0"
                  style={{
                    background: sending === q.id ? "#2a3942" : "#25D366",
                    color: sending === q.id ? "#8696a0" : "#111b21",
                  }}
                  data-testid={`wa-send-quote-${q.id}`}
                >
                  {sending === q.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                  {sending === q.id ? "..." : "Gönder"}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Yeni Sohbet Modal ────────────────────────────────────────────────────────
function NewChatModal({ onClose, onStarted }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [manualPhone, setManualPhone] = useState("");
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    api.get("/whatsapp/inbox/contacts").then(r => {
      setContacts(r.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const startChat = async (phone) => {
    const p = phone.trim();
    if (!p) return;
    setStarting(true);
    try {
      await api.post("/whatsapp/inbox/start-chat", { phone: p, text: "Merhaba" });
      toast.success("Konuşma başlatıldı");
      onStarted(p);
    } catch (e) {
      toast.error("Başlatılamadı: " + (e.response?.data?.detail || e.message));
    } finally { setStarting(false); }
  };

  const filtered = contacts.filter(c =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.phone.includes(search) || c.displayPhone?.includes(search)
  );
  const matchedContact = contacts.find(c =>
    manualPhone.length >= 10 && (c.phone.endsWith(manualPhone.slice(-10)) || manualPhone.endsWith(c.phone.slice(-10)))
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl flex flex-col"
        style={{ background: "#202c33", maxHeight: "80vh" }}
        onClick={e => e.stopPropagation()}
        data-testid="wa-new-chat-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 shrink-0" style={{ borderBottom: "1px solid #2a3942" }}>
          <h3 className="font-bold text-base" style={{ color: "#e9edef" }}>Yeni Sohbet Başlat</h3>
          <button onClick={onClose} style={{ color: "#8696a0" }}><X className="w-5 h-5" /></button>
        </div>

        {/* Manuel numara */}
        <div className="px-4 py-3 shrink-0" style={{ borderBottom: "1px solid #2a3942" }}>
          <label className="text-xs font-semibold mb-1.5 block" style={{ color: "#8696a0" }}>
            Numara ile başlat (ör: 905551234567)
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="9053XXXXXXXX"
              value={manualPhone}
              onChange={e => setManualPhone(e.target.value.replace(/[^0-9]/g, ""))}
              className="flex-1 px-3 py-2 rounded-lg text-sm outline-none"
              style={{ background: "#2a3942", color: "#e9edef", border: `1px solid ${matchedContact ? "#25D366" : "#3b4a54"}` }}
              data-testid="wa-manual-phone-input"
            />
            <button
              onClick={() => startChat(manualPhone)}
              disabled={starting || manualPhone.length < 10}
              className="px-3 py-2 rounded-lg text-sm font-semibold transition-colors"
              style={{ background: "#25D366", color: "#111b21" }}
            >
              {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
          {matchedContact && (
            <div className="mt-1.5 flex items-center gap-1.5 text-xs" style={{ color: "#25D366" }}>
              <User className="w-3 h-3" />
              <span>Kayıtlı müşteri: <strong>{matchedContact.name}</strong> ({matchedContact.type})</span>
            </div>
          )}
        </div>

        {/* Müşteri arama */}
        <div className="px-4 py-2 shrink-0">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: "#2a3942" }}>
            <Search className="w-3.5 h-3.5" style={{ color: "#8696a0" }} />
            <input
              type="text"
              placeholder="Müşteri ara..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="bg-transparent flex-1 text-sm outline-none"
              style={{ color: "#e9edef" }}
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#25D366" }} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 text-sm" style={{ color: "#8696a0" }}>
              {search ? "Sonuç yok" : "Telefonlu müşteri yok"}
            </div>
          ) : (
            filtered.map(c => (
              <button
                key={c.id}
                onClick={() => startChat(c.phone)}
                disabled={starting}
                className="flex items-center gap-3 w-full px-4 py-3 text-left transition-colors hover:bg-white/5"
                style={{ borderBottom: "1px solid #1a2428" }}
                data-testid={`wa-contact-${c.id}`}
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 text-white text-sm font-bold"
                  style={{ background: avatarColor(c.phone) }}>
                  {initials(c.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: "#e9edef" }}>{c.name}</p>
                  <p className="text-xs font-mono" style={{ color: "#8696a0" }}>{c.displayPhone || c.phone}</p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded" style={{ background: "#2a3942", color: "#8696a0" }}>
                  {c.type}
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ─── QR Bağlama Ekranı ────────────────────────────────────────────────────────
function QRLinkView() {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const pollRef = useRef(null);

  const fetchQR = useCallback(async () => {
    try {
      // Önce bağlantı durumuna bak (Meta veya Green API)
      const statusRes = await api.get("/whatsapp/inbox/connection-status").catch(() => null);
      if (statusRes?.data?.authorized) {
        setQrData({ status: "authorized" });
        setStatus("authorized");
        setLoading(false);
        clearInterval(pollRef.current);
        return;
      }
      const { data } = await api.get("/whatsapp/inbox/qr");
      setQrData(data);
      setStatus(data.status);
      if (data.status === "authorized") clearInterval(pollRef.current);
    } catch (e) {
      setStatus("error");
      setQrData({ status: "error", error: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchQR();
    pollRef.current = setInterval(fetchQR, 5000);
    return () => clearInterval(pollRef.current);
  }, [fetchQR]);

  return (
    <div className="flex-1 overflow-y-auto p-6" style={{ background: "#111b21" }}>
      <div className="max-w-lg mx-auto space-y-6">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-2"
            style={{ background: "rgba(37,211,102,0.1)" }}>
            <Smartphone className="w-7 h-7" style={{ color: "#25D366" }} />
          </div>
          <h2 className="text-lg font-extrabold" style={{ color: "#e9edef" }}>WhatsApp Cihaz Bağlama</h2>
          <p className="text-sm" style={{ color: "#8696a0" }}>
            Green API ile QR okutarak kişisel WhatsApp hesabınızı ERP'ye bağlayın.
          </p>
        </div>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="w-8 h-8 animate-spin" style={{ color: "#25D366" }} />
            <p className="text-sm" style={{ color: "#8696a0" }}>Yükleniyor...</p>
          </div>
        ) : status === "authorized" ? (
          <div className="flex flex-col items-center gap-4 py-8">
            <div className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ background: "rgba(37,211,102,0.1)" }}>
              <CheckCircle2 className="w-10 h-10" style={{ color: "#25D366" }} />
            </div>
            <div className="text-center">
              <p className="font-bold text-lg" style={{ color: "#25D366" }}>Bağlantı Aktif!</p>
              <p className="text-sm mt-1" style={{ color: "#8696a0" }}>WhatsApp başarıyla bağlandı.</p>
            </div>
          </div>
        ) : status === "error" ? (
          <div className="p-5 rounded-xl space-y-3" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)" }}>
            <div className="flex items-center gap-2 font-bold" style={{ color: "#ef4444" }}>
              <WifiOff className="w-4 h-4" /> Bağlantı hatası
            </div>
            <p className="text-sm" style={{ color: "#8696a0" }}>
              {qrData?.error || "Green API yapılandırması eksik."}
            </p>
            <button onClick={fetchQR} className="text-xs px-3 py-1.5 rounded-lg" style={{ background: "#2a3942", color: "#e9edef" }}>
              <RefreshCw className="w-3.5 h-3.5 inline mr-1" /> Tekrar Dene
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <div className="p-4 bg-white rounded-2xl shadow-lg">
              {qrData?.qr ? (
                <img
                  src={qrData.qr.startsWith("data:") ? qrData.qr : `data:image/png;base64,${qrData.qr}`}
                  alt="QR Kod"
                  className="w-56 h-56"
                  data-testid="qr-code-image"
                  onError={e => { e.target.style.display = "none"; }}
                />
              ) : (
                <div className="w-56 h-56 flex items-center justify-center bg-slate-100 rounded-lg">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: "#f5a623" }}>
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#f5a623" }} />
              Bağlantı bekleniyor... (5 sn'de bir yenileniyor)
            </div>
            <div className="w-full p-4 rounded-xl space-y-3" style={{ background: "#202c33" }}>
              <p className="text-xs font-bold" style={{ color: "#8696a0" }}>Nasıl bağlanılır?</p>
              {["Telefonunuzda WhatsApp'ı açın", "Sağ üst → 3 nokta → Bağlı cihazlar → Cihaz ekle", "QR kodu kameraya tutun", "Bağlantı otomatik kurulur"].map((s, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[11px] font-bold"
                    style={{ background: "rgba(37,211,102,0.15)", color: "#25D366" }}>
                    {i + 1}
                  </span>
                  <span className="text-xs" style={{ color: "#8696a0" }}>{s}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <button onClick={fetchQR} className="w-full py-2 rounded-xl text-xs font-semibold"
          style={{ background: "#2a3942", color: "#8696a0" }} data-testid="qr-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 inline mr-1" /> QR'ı Yenile
        </button>
      </div>
    </div>
  );
}
